import React, { useState, useEffect } from 'react';
import { predictWithModel, predictBatchWithModel, getModelsRegistry } from '../api';
import { EmptyState, ErrorState } from './shared/UIComponents';
import { useActiveModel } from '../context/ActiveModelContext';

const inferTaskTypeFromModelType = (modelType) => {
  if (!modelType) return 'unknown';
  const normalized = String(modelType).toLowerCase();
  if (normalized.includes('linear') || normalized.includes('regression')) return 'regression';
  if (normalized.includes('logistic') || normalized.includes('svm') || normalized.includes('forest') || normalized.includes('neural')) return 'classification';
  return 'unknown';
};

const ModelPrediction = ({ datasets, selectedDataset, modelTypes, selectedModel }) => {
  const [selectedModelId, setSelectedModelId] = useState('');
  const { activeModel } = useActiveModel();
  const [availableModels, setAvailableModels] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [batchResultInfo, setBatchResultInfo] = useState(null);
  const [singleFeatures, setSingleFeatures] = useState({});
  const MAX_MANUAL_FEATURES = 25; // Limit for manual feature input to prevent UI overload

  // Load available models when component mounts
  useEffect(() => {
    loadAvailableModels();
  }, []);

  const loadAvailableModels = async () => {
    try {
      const response = await getModelsRegistry();
      if (response && response.models) {
        const models = response.models.map(model => ({
          id: model.id,
          name: `${model.id} - ${model.model_type} - ${model.dataset_name}`,
          dataset: model.dataset_name,
          type: model.model_type,
          taskType: model.metadata?.task_type || model.task_type || inferTaskTypeFromModelType(model.model_type),
          decisionThreshold: model.decision_threshold ?? model.metadata?.decision_threshold ?? null,
          accuracy: model.metrics?.accuracy,
          created_at: model.created_at,
          featureNames: model.metadata?.feature_names || model.feature_names || []
        }));
        setAvailableModels(models);
      }
    } catch (err) {
      console.error('Failed to load models:', err);
      // Fallback to placeholder models if API fails
      const models = [
        { id: 'model_1', name: 'Random Forest - Dataset 1', dataset: 'dataset1', type: 'random_forest' },
        { id: 'model_2', name: 'SVM - Dataset 2', dataset: 'dataset2', type: 'svm' },
        { id: 'model_3', name: 'Neural Network - Dataset 3', dataset: 'dataset3', type: 'neural_network' }
      ];
      setAvailableModels(models);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setUploadedFile(file);
      setError(null);
    } else {
      setError('Please upload a valid CSV file');
      setUploadedFile(null);
    }
  };

  // Handle model selection change - default to active model context
  // If selectedModel prop changes, update selectedModelId accordingly
  useEffect(() => {
    if (activeModel) {
      setSelectedModelId(activeModel.id);
    }
  }, [activeModel]);
  const handleModelSelection = (event) => {
    setSelectedModelId(event.target.value);
    setError(null);
  };

  // Dynamically rebuild the input state dictionary when the selected model changes
  useEffect(() => {
    if (selectedModelId) {
      const model = availableModels.find(m => m.id === selectedModelId);
      if (model && model.featureNames && model.featureNames.length > 0) {
        const initialFeatures = {};
        model.featureNames.forEach(f => { initialFeatures[f] = ''; });
        setSingleFeatures(initialFeatures);
      } else {
        setSingleFeatures({});
      }
    } else {
      setSingleFeatures({});
    }
  }, [selectedModelId, availableModels]);

  const selectedModelDetails = availableModels.find((model) => model.id === selectedModelId) || null;
  const featuresToRender = selectedModelDetails?.featureNames?.length > 0 ? selectedModelDetails.featureNames : Object.keys(singleFeatures);


  const handleSinglePrediction = async () => {
    if (!selectedModelId) {
      setError('Please select a model');
      return;
    }

    // Validate that no required feature was left blank
    const missingFeature = featuresToRender.find(f => singleFeatures[f] === '' || singleFeatures[f] === undefined);
    if (missingFeature) {
      setError(`Please fill in all feature values. Missing: ${missingFeature}`);
      return;
    }

    // Convert string inputs to floats using the exact feature names
    const featureData = {};
    featuresToRender.forEach(f => {
      featureData[f] = parseFloat(singleFeatures[f]);
    });

    try {
      setLoading(true);
      setError(null);
      
      const result = await predictWithModel(selectedModelId, featureData);
      
      // Check if a prediction value or error key exists natively in the payload
      if (result && result.prediction !== undefined && !result.error) {
        setPredictions([result]); // Pass the whole object so the results card can render the values
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result.error || result.message || 'Prediction failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during prediction');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchPrediction = async () => {
    if (!selectedModelId) {
      setError('Please select a model');
      return;
    }

    if (!uploadedFile) {
      setError('Please upload a CSV file for batch prediction');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use the proper API function for batch prediction
      const result = await predictBatchWithModel(selectedModelId, uploadedFile);
      
      console.log('Batch prediction result:', result); // Debug log
      
      if (result.sample_predictions) {
        // Convert the sample predictions to the format expected by the UI
        const formattedPredictions = result.sample_predictions.map((pred, index) => {
          // Extract prediction value and probabilities
          const predictionValue = pred.prediction || 0;
          const probNeg = pred.probability_negative_class || null;
          const probPos = pred.probability_positive_class || null;
          
          // Get all feature columns (exclude prediction and probability columns)
          const featureColumns = Object.keys(pred).filter(key => 
            key !== 'prediction' && 
            key !== 'probability_negative_class' && 
            key !== 'probability_positive_class'
          );
          
          // Get the top 3 features by absolute value
          const sortedFeatures = featureColumns
            .map(featureName => ({
              name: featureName,
              value: Math.abs(parseFloat(pred[featureName]) || 0)
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 3);
          
          // Convert to the format expected by the prediction cards
          return {
            prediction: predictionValue,
            feature1: sortedFeatures[0]?.value || 0,
            feature2: sortedFeatures[1]?.value || 0,
            feature3: sortedFeatures[2]?.value || 0,
            feature1_name: sortedFeatures[0]?.name || 'Feature 1',
            feature2_name: sortedFeatures[1]?.name || 'Feature 2',
            feature3_name: sortedFeatures[2]?.name || 'Feature 3',
            probability_negative_class: probNeg,
            probability_positive_class: probPos
          };
        });
        
        setPredictions(formattedPredictions);
        setBatchResultInfo({
          totalPredictions: result.total_predictions,
          resultsFilename: result.results_filename,
          modelType: result.model_type,
          datasetName: result.dataset_name,
          predictionSummary: result.prediction_summary
        });
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        
        // Show additional info about the batch prediction
        console.log(`Batch prediction completed: ${result.total_predictions} total predictions`);
        console.log(`Results saved to: ${result.results_filename}`);
        
        // Trigger a custom event to notify other components to refresh
        window.dispatchEvent(new CustomEvent('batchPredictionCompleted', {
          detail: { resultsFilename: result.results_filename }
        }));
      } else {
        setError('No predictions returned from batch prediction');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during batch prediction');
    } finally {
      setLoading(false);
    }
  }

  const clearResults = () => {
    setPredictions(null);
    setBatchResultInfo(null);
    setError(null);
    setSuccess(false);
  };

  const selectedTaskType = (activeModel?.id === selectedModelId
    ? (activeModel?.metadata?.task_type || activeModel?.task_type)
    : selectedModelDetails?.taskType) || inferTaskTypeFromModelType(selectedModelDetails?.type);
  const selectedThreshold = (activeModel?.id === selectedModelId
    ? (activeModel?.decision_threshold ?? activeModel?.metadata?.decision_threshold)
    : selectedModelDetails?.decisionThreshold);

  return (
    <div className="prediction-container">
      <h2 className="prediction-container__title">
        Model Prediction
      </h2>
      
      <div className="prediction-container__content">
        {availableModels.length === 0 && (
          <div style={{ marginBottom: '16px', width: '100%' }}>
            <EmptyState
              title="No models available"
              description="Train and validate a model first, then return here to run predictions with confidence guidance."
              icon="🧠"
              actionLabel="Refresh models"
              actionAriaLabel="Refresh available models"
              onAction={loadAvailableModels}
              autoFocusAction={true}
            />
          </div>
        )}
        {/* Model Selection */}
        <div className="model-selection">
          <div className="model-selection__field">
            <div className="model-selection__header">
              <label className="model-selection__label">
                Select Model
              </label>
              <button
                onClick={loadAvailableModels}
                className="model-selection__refresh-btn"
                aria-label="Refresh available models"
              >
                ↻ Refresh
              </button>
            </div>
            <select
              value={selectedModelId}
              onChange={handleModelSelection}
              className="model-selection__select"
              aria-label="Select trained model for prediction"
            >
              <option value="">Choose a model...</option>
              {availableModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} {model.accuracy ? `(Acc: ${(model.accuracy * 100).toFixed(1)}%)` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Single Prediction Feature Inputs */}
          <div className="feature-inputs">
            <label className="feature-inputs__label">
              Feature Values (for single prediction)
            </label>
            
            {!selectedModelId ? (
              <p style={{ fontSize: '0.9rem', color: '#666' }}>Please select a model to view required features.</p>
            ) : featuresToRender.length > MAX_MANUAL_FEATURES ? (
              <div className="alert alert--warning" style={{ fontSize: '0.9rem', marginTop: '8px' }}>
                This model requires <strong>{featuresToRender.length}</strong> features. Manual entry is disabled to prevent browser freezing. Please upload a CSV file and use Batch Prediction instead.
              </div>
            ) : (
              <div className="feature-inputs__grid" style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '10px' }}>
                {featuresToRender.map((featureName) => (
                  <div key={featureName} className="feature-inputs__field">
                    <label className="feature-inputs__field-label" title={featureName}>
                      {featureName.length > 25 ? featureName.substring(0, 25) + '...' : featureName}
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={singleFeatures[featureName] ?? ''}
                      onChange={(e) => setSingleFeatures(prev => ({ ...prev, [featureName]: e.target.value }))}
                      className="feature-inputs__input"
                      placeholder="0.0"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="file-upload">
            <label className="file-upload__label">
              Upload Feature CSV (Optional for batch prediction)
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="file-upload__input"
            />
            {uploadedFile && (
              <p className="file-upload__status file-upload__status--success">
                ✓ {uploadedFile.name} uploaded
              </p>
            )}
          </div>
        </div>

        {/* Prediction Actions */}
        <div className="prediction-actions">
          <div className="prediction-actions__section">
            <h3 className="prediction-actions__title">
              Prediction Type
            </h3>
            <div className="prediction-actions__buttons">
              <button
                onClick={handleSinglePrediction}
                disabled={loading || !selectedModelId}
                className="btn btn--primary btn--full"
              >
                {loading ? 'Processing...' : 'Single Prediction'}
              </button>
              
              <button
                onClick={handleBatchPrediction}
                disabled={loading || !selectedModelId || !uploadedFile}
                className="btn btn--success btn--full"
              >
                {loading ? 'Processing...' : 'Batch Prediction'}
              </button>
            </div>
          </div>

          {success && (
            <div className="alert alert--success">
              Prediction completed successfully!
            </div>
          )}

          {batchResultInfo && (
            <div className="batch-result-info">
              <h4 className="batch-result-info__title">Batch Prediction Results</h4>
              <div className="batch-result-info__details">
                <div className="batch-result-info__item">
                  <strong>Total Predictions:</strong> {batchResultInfo.totalPredictions}
                </div>
                <div className="batch-result-info__item">
                  <strong>Results File:</strong> {batchResultInfo.resultsFilename}
                </div>
                <div className="batch-result-info__item">
                  <strong>Model:</strong> {batchResultInfo.modelType} ({batchResultInfo.datasetName})
                </div>
                {batchResultInfo.predictionSummary && (
                  <div className="batch-result-info__summary">
                    <strong>Prediction Summary:</strong>
                    <ul>
                      <li>Mean: {batchResultInfo.predictionSummary.mean_prediction?.toFixed(2)}</li>
                      <li>Std: {batchResultInfo.predictionSummary.std_prediction?.toFixed(2)}</li>
                      <li>Min: {batchResultInfo.predictionSummary.min_prediction?.toFixed(2)}</li>
                      <li>Max: {batchResultInfo.predictionSummary.max_prediction?.toFixed(2)}</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <ErrorState
              message={error}
              onRetry={() => {
                setError(null);
                if (uploadedFile) {
                  handleBatchPrediction();
                } else {
                  handleSinglePrediction();
                }
              }}
            />
          )}
        </div>
      </div>

      {/* Results Section */}
      {predictions && (
        <div className="results-section">
          <div className="results-section__header">
            <h3 className="results-section__title">
              Prediction Results
            </h3>
            <button
              onClick={clearResults}
              className="results-section__clear-btn"
            >
              Clear Results
            </button>
          </div>
          
                {/* Prediction Context */}
      <div className="prediction-context">
        <div className="prediction-context__header">
          <div className="prediction-context__icon">
            <svg className="icon icon--info" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="prediction-context__content">
            <h4 className="prediction-context__title">Prediction Interpretation</h4>
            <div className="prediction-context__description">
              <p><strong>Output guidance:</strong> use threshold and confidence together; treat low-confidence outputs as review candidates.</p>
              <p><strong>Task Type:</strong> {selectedTaskType === 'classification' ? 'Classification' : selectedTaskType === 'regression' ? 'Regression' : 'Unknown'}</p>
              {selectedTaskType === 'classification' ? (
                <>
                  <p><strong>Prediction Values:</strong> Class-oriented outputs (positive/negative or class probabilities)</p>
                  <p><strong>Interpretation:</strong> Confidence is highest when probabilities are far from the threshold and lowest near the boundary.</p>
                  <p><strong>Decision Threshold:</strong> {selectedThreshold != null ? Number(selectedThreshold).toFixed(2) : 'Default backend threshold'}</p>
                  <p><strong>Confidence bands:</strong> high (&gt;=80%), medium (60-79%), low (&lt;60%).</p>
                </>
              ) : (
                <>
                  <p><strong>Prediction Values:</strong> Continuous numerical scores</p>
                  <p><strong>Interpretation:</strong> Higher/lower values indicate relative magnitude based on training data scale.</p>
                  <p><strong>Scale:</strong> No fixed global range unless your dataset defines one; validate with known reference points.</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
          
          {/* Results Grid */}
          <div className="prediction-results">
            <div className="prediction-results__grid">
              {predictions.map((prediction, index) => {
                const sampleNumber = index + 1;
                const predValue = prediction.prediction;
                let outcomeLabel = 'Unknown';
                let outcomeClass = 'toxicity-badge--unknown';
                
                if (selectedTaskType === 'classification') {
                  const probPos = prediction.probability_positive_class;
                  const threshold = selectedThreshold != null ? Number(selectedThreshold) : 0.5;
                  if (probPos != null) {
                    outcomeLabel = probPos >= threshold ? 'Positive' : 'Negative';
                    outcomeClass = probPos >= threshold ? 'toxicity-badge--high' : 'toxicity-badge--low';
                  } else {
                    outcomeLabel = Number(predValue) >= threshold ? 'Positive' : 'Negative';
                    outcomeClass = Number(predValue) >= threshold ? 'toxicity-badge--high' : 'toxicity-badge--low';
                  }
                } else {
                  // Regression-style banding for quick readability
                  if (predValue !== undefined && predValue !== null) {
                    if (predValue < 50) {
                      outcomeLabel = 'Low';
                      outcomeClass = 'toxicity-badge--low';
                    } else if (predValue < 200) {
                      outcomeLabel = 'Medium';
                      outcomeClass = 'toxicity-badge--medium';
                    } else {
                      outcomeLabel = 'High';
                      outcomeClass = 'toxicity-badge--high';
                    }
                  }
                }
                
                return (
                  <div key={sampleNumber} className="prediction-card">
                    <div className="prediction-card__header">
                      <div className="prediction-card__sample-label">Sample {sampleNumber}</div>
                      <span className={`toxicity-badge ${outcomeClass}`}>
                        {outcomeLabel}
                      </span>
                    </div>
                    
                    <div className="prediction-card__main">
                      <div className="prediction-card__value">
                        {predValue !== undefined && predValue !== null ? predValue.toFixed(2) : 'N/A'}
                      </div>
                      <div className="prediction-card__label">
                        {selectedTaskType === 'classification' ? 'Prediction Output' : 'Prediction Score'}
                      </div>
                    </div>
                    
                    {/* Feature Values */}
                    {prediction.feature1 !== undefined && (
                      <div className="prediction-card__features">
                        <div className="feature-item">
                          {prediction.feature1_name || 'Feature 1'}: {prediction.feature1?.toFixed(3) || 'N/A'}
                        </div>
                        <div className="feature-item">
                          {prediction.feature2_name || 'Feature 2'}: {prediction.feature2?.toFixed(3) || 'N/A'}
                        </div>
                        <div className="feature-item">
                          {prediction.feature3_name || 'Feature 3'}: {prediction.feature3?.toFixed(3) || 'N/A'}
                        </div>
                      </div>
                    )}
                    
                    {/* Probabilities for classification models */}
                    {prediction.probability_positive_class != null && (
                      <div className="prediction-card__probabilities">
                        <div className="probabilities__label">Confidence Scores:</div>
                        <div className="probability-item">
                          Positive: {(prediction.probability_positive_class * 100).toFixed(1)}%
                        </div>
                        {prediction.probability_negative_class != null && (
                          <div className="probability-item">
                            Negative: {(prediction.probability_negative_class * 100).toFixed(1)}%
                          </div>
                        )}
                        <div className="probability-item">
                          Confidence band: {Math.max(
                            prediction.probability_positive_class || 0,
                            prediction.probability_negative_class || 0
                          ) >= 0.8 ? 'High' : Math.max(
                            prediction.probability_positive_class || 0,
                            prediction.probability_negative_class || 0
                          ) >= 0.6 ? 'Medium' : 'Low'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="prediction-instructions">
        <h4 className="prediction-instructions__title">
          How to use:
        </h4>
        <ul className="prediction-instructions__list">
          <li>• Select a previously trained model from the dropdown</li>
          <li>• For single prediction: Use the "Single Prediction" button</li>
          <li>• For batch prediction: Upload a CSV file with features and use "Batch Prediction"</li>
          <li>• CSV should contain only feature columns (no target column)</li>
        </ul>
      </div>
    </div>
  );
};

export default ModelPrediction;
